/**
 * Afrikaans: Tickets
 *
 * What this file covers: The Tickets menu: queue, board, a ticket, tasks, reports and ticket settings.
 *
 * Tone: Calm, professional helpdesk wording. Keep a clear difference between a reply (the client sees it) and a
 * note (only the team sees it). Status names are short.
 *
 * Afrikaans: Standard South African Afrikaans. Address the reader as "jy". Use the same Afrikaans word for the
 * same English term everywhere; menu.ts and common.ts set the words the other files follow. Afrikaans must stay
 * complete: the release checks fail if a key is missing.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const tickets: Catalog = {
  'tickets.title': 'Kaartjies',
  'tickets.description': 'Elke navraag wat opvolg nodig het, van elke kanaal, in een tou.',
  'tickets.readOnly': 'Net die eienaar en administrateurs kan hierdie instellings verander. Jy kan dit hier sien.',
  'tickets.me': 'Ek',
  'tickets.unassigned': 'Nie toegewys nie',
  'tickets.noTeam': 'Geen span',
  'tickets.noClient': 'Geen kliënt',
  'tickets.active': 'Aktief',
  'tickets.inactive': 'Onaktief',
  'tickets.default': 'Verstek',
  'tickets.order': 'Volgorde',
  'tickets.orderHint': 'Laer getalle kom eerste.',
  'tickets.moveUp': 'Skuif op',
  'tickets.moveDown': 'Skuif af',
  'tickets.discard.title': 'Gooi jou veranderinge weg?',
  'tickets.discard.body': 'Wat jy hier verander het, is nie gestoor nie.',
  'tickets.category.new': 'Nuut',
  'tickets.category.open': 'Oop',
  'tickets.category.pending': 'Wag op kliënt',
  'tickets.category.on_hold': 'Opgeskort',
  'tickets.category.solved': 'Opgelos',
  'tickets.category.closed': 'Gesluit',
  'tickets.channel.whatsapp': 'WhatsApp',
  'tickets.channel.messenger': 'Messenger',
  'tickets.channel.instagram': 'Instagram',
  'tickets.channel.web': 'Webwerf-klets',
  'tickets.channel.email': 'E-pos',
  'tickets.channel.sms': 'SMS',
  'tickets.channel.voice': 'Oproep',
  'tickets.channel.console': 'Konsole',
  'tickets.source.console': 'Deur \'n persoon oopgemaak',
  'tickets.source.flow': 'Deur die kletsbot oopgemaak',
  'tickets.source.voice': 'Tydens \'n oproep oopgemaak',
  'tickets.source.rule': 'Deur \'n outomatisering oopgemaak',
  'tickets.view.all': 'Almal',
  'tickets.view.mine': 'Myne',
  'tickets.view.team': 'My spanne',
  'tickets.view.unassigned': 'Nie toegewys nie',
  'tickets.view.breaching': 'Teiken amper verby',
  'tickets.error.PLAN_LIMIT_REACHED': 'Jou plan laat nie meer hiervan toe nie. Gradeer jou plan op, of skakel eers een af.',
  'tickets.error.IN_USE': 'Kaartjies gebruik dit nog. Skuif hulle eers na \'n ander een.',
  'tickets.error.MESSAGING_WINDOW_CLOSED': 'Die antwoordvenster op hierdie kanaal het gesluit, so \'n vrye-teks-antwoord kan nie gestuur word nie.',
  'tickets.error.NO_CONVERSATION': 'Hierdie kaartjie het geen gesprek om op te antwoord nie. Koppel eers een.',
  'tickets.error.forbidden': 'Jy het nie toestemming om dit te doen nie.',
  'tickets.error.notFound': 'Daardie kaartjie kon nie gevind word nie. Dit is dalk saamgevoeg, of jy het nie toegang daartoe nie.',
  'tickets.error.unavailable': 'Kaartjies is nou nie beskikbaar nie. Probeer weer oor \'n oomblik.',
  'tickets.duration.minutes': '{{m}} min',
  'tickets.duration.hours': '{{h}} h {{m}} min',
  'tickets.duration.days': '{{d}} d {{h}} h',
  'tickets.field.number': 'Nommer',
  'tickets.field.subject': 'Onderwerp',
  'tickets.field.type': 'Soort',
  'tickets.field.priority': 'Prioriteit',
  'tickets.field.team': 'Span',
  'tickets.field.assignee': 'Toegewys aan',
  'tickets.field.tag': 'Etiket',
  'tickets.field.tags': 'Etikette',
  'tickets.field.due': 'Sperdatum',
  'tickets.field.dueHint': 'In jou besigheid se tydsone ({{zone}}).',
  'tickets.field.sla': 'Reaksieteiken',
  'tickets.field.client': 'Kliënt',
  'tickets.field.clientName': 'Kliënt se naam',
  'tickets.field.channel': 'Kanaal',
  'tickets.field.source': 'Bron',
  'tickets.field.choose': 'Kies…',
  'tickets.field.requiredError': 'Hierdie veld is verpligtend.',
  'tickets.filter.anyStatus': 'Enige status',
  'tickets.filter.anyType': 'Enige soort',
  'tickets.filter.anyPriority': 'Enige prioriteit',
  'tickets.filter.anyTeam': 'Enige span',
  'tickets.filter.anyone': 'Enigiemand',
  'tickets.filter.tag': 'Etiket',
  'tickets.queue.title': 'Tou',
  'tickets.queue.search': 'Soek op nommer, onderwerp of kliënt',
  'tickets.queue.filtering': 'Gefiltreer',
  'tickets.queue.emptyFiltered': 'Geen kaartjies pas by hierdie filters nie',
  'tickets.queue.emptyFilteredHint': 'Maak die filters skoon om elke kaartjie in hierdie aansig te sien.',
  'tickets.queue.emptyHint': 'Kaartjies verskyn hier wanneer jou span, die kletsbot of \'n outomatisering een oopmaak.',
  'tickets.queue.empty.all': 'Nog geen kaartjies nie',
  'tickets.queue.empty.mine': 'Niks is aan jou toegewys nie',
  'tickets.queue.empty.team': 'Jou spanne het geen kaartjies nie',
  'tickets.queue.empty.unassigned': 'Iemand werk aan elke kaartjie',
  'tickets.queue.empty.breaching': 'Niks is naby sy reaksieteiken nie',
  'tickets.bulk.selected': 'Gekies: {{count}}',
  'tickets.bulk.assign': 'Wys toe aan…',
  'tickets.bulk.status': 'Stel status…',
  'tickets.bulk.done': '{{count}} kaartjies is bygewerk.',
  'tickets.bulk.someFailed': '{{failed}} van {{total}} kaartjies is nie bygewerk nie: {{reason}}',
  'tickets.bulk.selectAll': 'Kies elke kaartjie wat gewys word',
  'tickets.bulk.selectOne': 'Kies kaartjie {{number}}',
  'tickets.create.title': 'Nuwe kaartjie',
  'tickets.create.submit': 'Skep kaartjie',
  'tickets.create.done': 'Kaartjie {{number}} is geskep.',
  'tickets.create.subjectRequired': 'Gee die kaartjie \'n onderwerp.',
  'tickets.create.clientNameHint': 'Opsioneel. Kaartjies wat uit \'n gesprek oopgemaak word, word outomaties aan die kliënt gekoppel.',
  'tickets.create.notify': 'Laat die kliënt weet hul versoek is ontvang',
  'tickets.create.notifyHint': 'Stuur die kaartjienommer op hierdie gesprek.',
  'tickets.create.noTypes': 'Daar is geen aktiewe kaartjiesoorte nie. Die versteksoort word gebruik.',
  'tickets.sla.title': 'Reaksieteikens',
  'tickets.sla.firstResponse': 'Eerste reaksie',
  'tickets.sla.resolution': 'Oplossing',
  'tickets.sla.dueIn': 'Oor {{time}}',
  'tickets.sla.overdue': 'Agterstallig',
  'tickets.sla.overdueBy': '{{time}} agterstallig',
  'tickets.sla.paused': 'Gepouseer',
  'tickets.sla.pausedHint': 'Die klok staan stil terwyl die kaartjie op die kliënt wag of opgeskort is.',
  'tickets.sla.breachedHint': '\'n Reaksieteiken op hierdie kaartjie is gemis.',
  'tickets.sla.dueAt': '{{phase}} teen {{when}}',
  'tickets.sla.dueOn': 'Teen {{when}}',
  'tickets.sla.respondedAt': 'Gereageer {{when}}',
  'tickets.sla.solvedAt': 'Opgelos {{when}}',
  'tickets.sla.noPolicy': 'Geen reaksieteiken geld vir hierdie kaartjie nie.',
  'tickets.sla.policies': 'Reaksieteikens',
  'tickets.sla.policiesIntro': 'Hoe vinnig jou span belowe om te reageer en op te los, volgens soort en prioriteit.',
  'tickets.sla.add': 'Voeg \'n reaksieteiken by',
  'tickets.sla.empty': 'Nog geen reaksieteikens nie',
  'tickets.sla.emptyHint': 'Voeg een by om sperdatums en agterstallige kaartjies in die tou te sien.',
  'tickets.sla.anyType': 'Enige soort',
  'tickets.sla.anyPriority': 'Enige prioriteit',
  'tickets.sla.clock': 'Klok',
  'tickets.sla.businessHours': 'Besigheidsure',
  'tickets.sla.allHours': 'Dag en nag',
  'tickets.sla.firstResponseHint': 'Laat leeg vir geen eerste-reaksie-teiken nie.',
  'tickets.sla.resolutionHint': 'Laat leeg vir geen oplossingsteiken nie.',
  'tickets.sla.businessHoursOnly': 'Tel net besigheidsure',
  'tickets.sla.businessHoursOnlyHint': 'Tyd buite jou besigheidsure en sluitings tel nie.',
  'tickets.sla.orderHint': '\'n Kaartjie kry die eerste aktiewe teiken, in hierdie volgorde, waarvan die soort en prioriteit pas.',
  'tickets.sla.error.times': 'Stel \'n eerste-reaksie- of \'n oplossingstyd.',
  'tickets.unit.label': 'Eenheid',
  'tickets.unit.minutes': 'Minute',
  'tickets.unit.hours': 'Ure',
  'tickets.board.title': 'Bord',
  'tickets.board.description': 'Kaartjies volgens waar hulle staan. Sleep \'n kaart na \'n ander kolom om sy status te verander.',
  'tickets.board.viewLabel': 'Watter kaartjies',
  'tickets.board.hint': 'Gesluite kaartjies verlaat die bord.',
  'tickets.board.empty': 'Geen kaartjies hier nie',
  'tickets.board.more': 'Die eerste {{n}} word gewys. Gebruik die tou om die res te sien.',
  'tickets.board.open': 'Maak kaartjie {{number}} oop: {{subject}}',
  'tickets.board.moving': 'Skuif na {{status}}…',
  'tickets.board.moved': '{{number}} is nou {{status}}.',
  'tickets.board.noStatus': 'Die soort {{type}} het geen {{category}}-status nie. Voeg een by onder Kaartjie-instellings.',
  'tickets.board.chooseTitle': 'Watter status?',
  'tickets.board.chooseBody': 'Die soort {{type}} het meer as een status hier. Kies een vir {{number}}.',
  'tickets.board.moveHere': 'Skuif',
  'tickets.detail.backToQueue': 'Terug na die tou',
  'tickets.detail.saved': 'Gestoor.',
  'tickets.detail.copyNumber': 'Kopieer nommer',
  'tickets.detail.opened': 'Oopgemaak {{when}} vir {{client}} op {{channel}}',
  'tickets.detail.mergedInto': 'Hierdie kaartjie is by \'n ander een saamgevoeg. Die werk gaan daar voort.',
  'tickets.detail.openMerged': 'Maak daardie kaartjie oop',
  'tickets.detail.changeType.title': 'Verander die soort?',
  'tickets.detail.changeType.body': 'Verander hierdie kaartjie van {{from}} na {{to}}? Sy velde en reaksieteikens volg die nuwe soort.',
  'tickets.detail.changeType.confirm': 'Verander soort',
  'tickets.detail.noTags': 'Geen etikette',
  'tickets.detail.addTag': 'Voeg \'n etiket by',
  'tickets.detail.removeTag': 'Verwyder etiket {{tag}}',
  'tickets.detail.details': 'Besonderhede',
  'tickets.detail.openContact': 'Maak kontak oop',
  'tickets.detail.closedAt': 'Gesluit',
  'tickets.fields.title': '{{type}}-velde',
  'tickets.fields.missing': 'Nog nie ingevul nie',
  'tickets.csat.title': 'Tevredenheid',
  'tickets.csat.score': '{{score}} uit 5',
  'tickets.csat.none': 'Nog geen gradering nie',
  'tickets.timeline.title': 'Tydlyn',
  'tickets.timeline.all': 'Alles',
  'tickets.timeline.messages': 'Boodskappe',
  'tickets.timeline.notes': 'Notas',
  'tickets.timeline.changes': 'Veranderinge',
  'tickets.timeline.empty': 'Nog niks hier nie.',
  'tickets.timeline.messagesFailed': 'Die gesprek se boodskappe kon nie gelaai word nie.',
  'tickets.timeline.fromClient': 'Van die kliënt',
  'tickets.timeline.toClient': 'Aan die kliënt',
  'tickets.timeline.note': 'Interne nota',
  'tickets.timeline.someone': 'Iemand',
  'tickets.timeline.system': 'Stelsel',
  'tickets.timeline.template': 'Sjabloon: {{name}}',
  'tickets.timeline.attachment': 'Aanhegsel: {{name}}',
  'tickets.timeline.media': 'Mediaboodskap',
  'tickets.timeline.notDelivered': 'Nie afgelewer nie',
  'tickets.activity.reply': 'het die kliënt geantwoord',
  'tickets.activity.client_message': 'het geskryf',
  'tickets.activity.note': 'het \'n nota bygevoeg',
  'tickets.activity.status': 'het die status verander',
  'tickets.activity.priority': 'het die prioriteit verander',
  'tickets.activity.assignee': 'het verander aan wie dit toegewys is',
  'tickets.activity.team': 'het die span verander',
  'tickets.activity.field': 'het \'n veld verander',
  'tickets.activity.task': 'het \'n taak bygewerk',
  'tickets.activity.system': 'het \'n verandering gemaak',
  'tickets.activity.csat': 'het die diens gegradeer',
  'tickets.activity.merge': 'het kaartjies saamgevoeg',
  'tickets.activity.link': 'het \'n gesprek gekoppel',
  'tickets.composer.reply': 'Antwoord',
  'tickets.composer.note': 'Nota',
  'tickets.composer.replyPlaceholder': 'Skryf aan die kliënt…',
  'tickets.composer.notePlaceholder': 'Skryf \'n nota vir jou span…',
  'tickets.composer.replyHint': 'Gaan na die kliënt op die gekoppelde gesprek. Ctrl of Cmd en Enter stuur.',
  'tickets.composer.noteHint': 'Net jou span sien notas. Die kliënt sien dit nooit nie.',
  'tickets.composer.send': 'Stuur antwoord',
  'tickets.composer.addNote': 'Voeg nota by',
  'tickets.composer.sent': 'Antwoord gestuur.',
  'tickets.composer.noted': 'Nota bygevoeg.',
  'tickets.composer.on': 'Antwoord op',
  'tickets.composer.conversation': '{{channel}}-gesprek {{n}}',
  'tickets.composer.windowClosed.title': 'Die antwoordvenster het gesluit',
  'tickets.composer.windowClosed.body': 'Dit is te lank sedert die kliënt laas op hierdie kanaal geskryf het, so net \'n goedgekeurde sjabloon bereik hulle nou. Stuur een vanuit die inkassie om die gesprek te heropen.',
  'tickets.composer.windowClosed.action': 'Stuur \'n sjabloon vanuit die inkassie',
  'tickets.tasks.title': 'Take',
  'tickets.tasks.add': 'Voeg taak by',
  'tickets.tasks.noneOpen': 'Geen oop take nie.',
  'tickets.tasks.showDone': 'Wys voltooide take ({{count}})',
  'tickets.tasks.hideDone': 'Versteek voltooide take',
  'tickets.task.kind': 'Soort',
  'tickets.task.kind.call': 'Oproep',
  'tickets.task.kind.question': 'Vra die kliënt',
  'tickets.task.kind.callout': 'Uitroep',
  'tickets.task.kind.custom': 'Ander',
  'tickets.task.kindHint.call': '\'n Taak met \'n sperdatum, soos om die kliënt terug te bel.',
  'tickets.task.kindHint.question': 'Die vraag word nou aan die kliënt gestuur, en die kaartjie wag vir hul antwoord.',
  'tickets.task.kindHint.callout': 'Bespreek iemand om na die kliënt uit te gaan, met jou besprekings.',
  'tickets.task.kindHint.custom': 'Enigiets anders wat jou span moet doen.',
  'tickets.task.defaultTitle.call': 'Bel die kliënt terug',
  'tickets.task.defaultTitle.question': 'Vra die kliënt \'n vraag',
  'tickets.task.defaultTitle.callout': 'Uitroep na die kliënt',
  'tickets.task.defaultTitle.custom': 'Volg op',
  'tickets.task.title': 'Taak',
  'tickets.task.due': 'Sperdatum',
  'tickets.task.question': 'Vraag aan die kliënt',
  'tickets.task.questionHint': 'Word op die kaartjie se gesprek gestuur sodra jy die taak byvoeg.',
  'tickets.task.service': 'Diens',
  'tickets.task.person': 'Wie gaan',
  'tickets.task.anyPerson': 'Wie ook al beskikbaar is',
  'tickets.task.address': 'Adres',
  'tickets.task.addressHint': 'Waarheen om te gaan. Word uit die kaartjie ingevul as dit \'n adres het.',
  'tickets.task.noServices': 'Daar is nog geen bespreekbare dienste nie.',
  'tickets.task.setUpServices': 'Stel dienste op',
  'tickets.task.askNow': 'Vra nou',
  'tickets.task.book': 'Bespreek uitroep',
  'tickets.task.created': 'Taak bygevoeg: {{title}}',
  'tickets.task.error.title': 'Gee die taak \'n naam.',
  'tickets.task.error.question': 'Skryf die vraag om te stuur.',
  'tickets.task.error.slot': 'Kies \'n tyd.',
  'tickets.task.error.address': 'Voeg die adres by waarheen gegaan moet word.',
  'tickets.task.asked': 'Gevra: {{question}}',
  'tickets.task.dueWhen': 'Teen {{when}}',
  'tickets.task.overdueSince': 'Agterstallig sedert {{when}}',
  'tickets.task.noDue': 'Geen sperdatum',
  'tickets.task.openBooking': 'Maak die bespreking oop',
  'tickets.task.markDone': 'Merk as klaar',
  'tickets.task.doneTitle': 'Merk die taak as klaar',
  'tickets.task.outcome': 'Uitkoms',
  'tickets.task.outcomeHint': 'Opsioneel. Wat gebeur het, vir die res van die span.',
  'tickets.task.doneToast': 'Klaar: {{title}}',
  'tickets.task.doneBy': 'Klaar gemaak deur {{who}}, {{when}}',
  'tickets.task.outcomeIs': 'Uitkoms: {{outcome}}',
  'tickets.task.reopen': 'Heropen',
  'tickets.link.title': 'Gesprekke',
  'tickets.link.action': 'Koppel \'n gesprek',
  'tickets.link.none': 'Geen gesprek is gekoppel nie.',
  'tickets.link.openInInbox': 'Maak oop in die inkassie',
  'tickets.link.search': 'Vind \'n gesprek',
  'tickets.link.searchHint': 'Soek op naam, nommer of die laaste boodskap.',
  'tickets.link.noMatch': 'Geen ander gesprekke pas nie.',
  'tickets.link.link': 'Koppel',
  'tickets.link.done': 'Gesprek gekoppel.',
  'tickets.merge.action': 'Voeg saam',
  'tickets.merge.title': 'Voeg {{number}} by \'n ander kaartjie saam',
  'tickets.merge.search': 'Kaartjienommer',
  'tickets.merge.searchHint': 'Tik minstens twee karakters van die nommer of die onderwerp.',
  'tickets.merge.noMatch': 'Geen oop kaartjie pas nie.',
  'tickets.merge.warning': '{{from}} skuif sy aktiwiteit, take en gesprekke na {{into}} en word nie meer aan gewerk nie. Dit kan nie ongedaan gemaak word nie.',
  'tickets.merge.confirm': 'Voeg saam by {{into}}',
  'tickets.merge.done': '{{from}} is by {{into}} saamgevoeg.',
  'tickets.myTasks.title': 'My take',
  'tickets.myTasks.description': 'Oproepe, vrae en uitroepe wat op jou wag, volgens sperdatum.',
  'tickets.myTasks.mine': 'Myne',
  'tickets.myTasks.everyone': 'Almal',
  'tickets.myTasks.open': 'Oop',
  'tickets.myTasks.all': 'Oop en klaar',
  'tickets.myTasks.overdueCount': 'Agterstallig: {{count}}',
  'tickets.myTasks.ticket': 'Kaartjie',
  'tickets.myTasks.done': 'Klaar',
  'tickets.myTasks.empty': 'Geen take nie',
  'tickets.myTasks.emptyHint': 'Take wat by kaartjies gevoeg word, verskyn hier.',
  'tickets.reports.title': 'Kaartjieverslae',
  'tickets.reports.description': 'Hoeveel kaartjies ingekom het en opgelos is, hoe vinnig, en hoe kliënte die diens gegradeer het.',
  'tickets.reports.last': 'Laaste {{n}} dae',
  'tickets.reports.custom': 'Eie keuse',
  'tickets.reports.from': 'Van',
  'tickets.reports.to': 'Tot',
  'tickets.reports.badRange': 'Die einddatum is voor die begindatum.',
  'tickets.reports.created': 'Geskep',
  'tickets.reports.solved': 'Opgelos',
  'tickets.reports.firstResponse': 'Eerste reaksie',
  'tickets.reports.resolution': 'Oplossing',
  'tickets.reports.average': 'Gemiddeld',
  'tickets.reports.slaMet': 'Teikens gehaal',
  'tickets.reports.slaMetHint': 'Kaartjies wat hul reaksieteikens gehaal het',
  'tickets.reports.csat': 'Tevredenheid',
  'tickets.reports.csatHint': 'Gemiddeld van {{count}} graderings uit 5',
  'tickets.reports.openNow': 'Nou oop',
  'tickets.reports.byDay': 'Geskep en opgelos per dag',
  'tickets.reports.noDays': 'Geen kaartjies in hierdie tydperk nie',
  'tickets.reports.noDaysHint': 'Kies \'n langer tydperk, of kyk weer sodra kaartjies inkom.',
  'tickets.reports.byTeam': 'Per span',
  'tickets.reports.byPerson': 'Per persoon',
  'tickets.reports.noRows': 'Niks om vir hierdie tydperk te wys nie.',
  'tickets.settings.title': 'Kaartjie-instellings',
  'tickets.settings.description': 'Nommering, wie wat sien, wat kliënte vertel word, en hoe kaartjies gesorteer en hanteer word.',
  'tickets.settings.section.general': 'Algemeen',
  'tickets.settings.section.updates': 'Kliëntopdaterings',
  'tickets.settings.section.satisfaction': 'Tevredenheid',
  'tickets.settings.section.types': 'Soorte en velde',
  'tickets.settings.section.statuses': 'Statusse',
  'tickets.settings.section.priorities': 'Prioriteite',
  'tickets.settings.section.sla': 'Reaksieteikens',
  'tickets.settings.section.automations': 'Outomatiserings',
  'tickets.settings.general': 'Algemeen',
  'tickets.settings.prefix': 'Nommervoorvoegsel',
  'tickets.settings.prefixHint': 'Tot agt letters, syfers, koppeltekens of skuinsstrepe, soos T-. Kaartjies word genommer soos {{example}}.',
  'tickets.settings.error.prefix': 'Gebruik tot agt letters, syfers, koppeltekens of skuinsstrepe.',
  'tickets.settings.error.name': 'Gee dit \'n naam.',
  'tickets.settings.visibility': 'Wie sien watter kaartjies',
  'tickets.settings.visibility.all': 'Almal sien elke kaartjie',
  'tickets.settings.visibility.team': 'Agente sien hul spanne se kaartjies',
  'tickets.settings.visibilityHint': 'Met spanne sien \'n agent hul spanne se kaartjies, kaartjies wat aan hulle toegewys is en kaartjies sonder \'n span. Die eienaar en administrateurs sien alles.',
  'tickets.settings.autoClose': 'Sluit opgeloste kaartjies na (dae)',
  'tickets.settings.autoCloseHint': 'Laat leeg of 0 om opgeloste kaartjies te hou totdat iemand hulle sluit.',
  'tickets.settings.reopen': 'Heropen \'n opgeloste kaartjie wanneer die kliënt antwoord',
  'tickets.settings.reopenHint': '\'n Gesluite kaartjie word nooit heropen nie. \'n Outomatisering kan eerder \'n nuwe een oopmaak.',
  'tickets.settings.updates': 'Kliëntopdaterings',
  'tickets.settings.updatesIntro': 'Wat kliënte oor hul kaartjie vertel word, op die gesprek waaruit dit gekom het.',
  'tickets.settings.notifyCreated': 'Wanneer \'n kaartjie oopgemaak word',
  'tickets.settings.notifyCreatedHint': 'Stuur die kaartjienommer sodat die kliënt dit kan aanhaal.',
  'tickets.settings.notifyStatus': 'Wanneer die status verander',
  'tickets.settings.notifyStatusHint': 'Stuur die nuwe status se kliëntbewoording.',
  'tickets.settings.notifySolved': 'Wanneer die kaartjie opgelos is',
  'tickets.settings.notifySolvedHint': 'Stuur die opgeloste status se kliëntbewoording.',
  'tickets.settings.updatesHint': 'Opdaterings gaan in Engels of Afrikaans, volgens jou besigheid se taal. \'n Boodskap wat nie afgelewer kan word nie, word op die kaartjie aangeteken.',
  'tickets.settings.satisfaction': 'Tevredenheid',
  'tickets.settings.satisfactionIntro': 'Vra die kliënt om die diens te gradeer wanneer hul kaartjie opgelos is.',
  'tickets.settings.csatEnabled': 'Vra vir \'n gradering by oplossing',
  'tickets.settings.csatQuestion': 'Die vraag',
  'tickets.settings.csatDefault': 'Hoe het ons gevaar? Antwoord met \'n getal van 1 (swak) tot 5 (uitstekend).',
  'tickets.settings.csatQuestionHint': 'Laat leeg om die vraag wat gewys word te gebruik. \'n Antwoord van 1 tot 5 binne drie dae word aangeteken.',
  'tickets.settings.fixFirst': 'Maak eers die gemerkte velde reg.',
  'tickets.settings.toast.saved': 'Kaartjie-instellings gestoor.',
  'tickets.settings.toast.savedNamed': '{{name}} gestoor.',
  'tickets.settings.toast.deleted': '{{name}} geskrap.',
  'tickets.settings.delete.title': 'Skrap {{name}}?',
  'tickets.settings.delete.body': '{{name}} sal geskrap word. Dit kan nie ongedaan gemaak word nie.',
  'tickets.settings.deleteNamed': 'Skrap {{name}}',
  'tickets.settings.limits': 'Planlimiete',
  'tickets.settings.limitTypes': 'Kaartjiesoorte: tot {{n}}',
  'tickets.settings.limitTypesNone': 'Kaartjiesoorte: geen limiet',
  'tickets.settings.limitRules': 'Aktiewe outomatiserings: tot {{n}}',
  'tickets.settings.limitRulesNone': 'Aktiewe outomatiserings: geen limiet',
  'tickets.settings.limitsHint': 'Gradeer jou plan onder Fakturering op vir meer.',
  'tickets.types.title': 'Soorte en velde',
  'tickets.types.intro': 'Soorte kaartjies, elk met sy eie velde, verstekspan en prioriteit.',
  'tickets.types.introLimit': 'Soorte kaartjies, elk met sy eie velde. {{used}} van {{limit}} in gebruik.',
  'tickets.types.limitHint': 'Aktiewe soorte: {{active}}. Onaktiewe soorte tel steeds teen die limiet totdat hulle geskrap word.',
  'tickets.types.add': 'Voeg soort by',
  'tickets.types.new': 'Nuwe kaartjiesoort',
  'tickets.types.create': 'Skep soort',
  'tickets.types.empty': 'Nog geen kaartjiesoorte nie',
  'tickets.types.emptyHint': '\'n Algemene soort word met die eerste gebruik vir jou gemaak.',
  'tickets.types.fields': 'Velde',
  'tickets.types.fieldsHint': 'Inligting wat jou span op kaartjies van hierdie soort invul.',
  'tickets.types.noFields': 'Geen velde nie. Kaartjies van hierdie soort het net \'n onderwerp en beskrywing.',
  'tickets.types.fieldLabel': 'Etiket',
  'tickets.types.fieldKey': 'Sleutel: {{key}}',
  'tickets.types.options': 'Keuses',
  'tickets.types.optionsHint': 'Skei die keuses met kommas.',
  'tickets.types.clientVisible': 'Die kliënt mag dit hoor',
  'tickets.types.removeField': 'Verwyder die veld {{label}}',
  'tickets.types.addField': 'Voeg \'n veld by',
  'tickets.types.colour': 'Kleur',
  'tickets.types.descriptionHint': 'Vir jou span: wanneer om hierdie soort te gebruik.',
  'tickets.types.defaultTeam': 'Verstekspan',
  'tickets.types.defaultTeamHint': 'Nuwe kaartjies van hierdie soort gaan na hierdie span.',
  'tickets.types.defaultPriority': 'Verstekprioriteit',
  'tickets.types.orgDefault': 'Die gewone verstek',
  'tickets.types.activeHint': '\'n Onaktiewe soort word nie vir nuwe kaartjies aangebied nie.',
  'tickets.types.delete.title': 'Skrap hierdie soort?',
  'tickets.types.delete.body': '{{name}} sal nie meer aangebied word nie. Kaartjies wat dit het, hou dit.',
  'tickets.types.toast.saved': '{{name}} gestoor.',
  'tickets.types.toast.created': '{{name}} geskep.',
  'tickets.types.toast.deleted': '{{name}} geskrap.',
  'tickets.types.error.name': 'Gee die soort \'n naam.',
  'tickets.types.error.fieldLabel': 'Elke veld het \'n etiket nodig.',
  'tickets.types.error.options': '\'n Lysveld het minstens twee keuses nodig.',
  'tickets.fieldType.text': 'Teks',
  'tickets.fieldType.number': 'Getal',
  'tickets.fieldType.date': 'Datum',
  'tickets.fieldType.select': 'Een uit \'n lys',
  'tickets.fieldType.multi_select': 'Verskeie uit \'n lys',
  'tickets.fieldType.yes_no': 'Ja of nee',
  'tickets.fieldType.address': 'Adres',
  'tickets.statuses.title': 'Statusse',
  'tickets.statuses.intro': 'Elke status behoort aan \'n stadium. Kliënte sien die kliëntbewoording, nooit die naam nie.',
  'tickets.statuses.add': 'Voeg status by',
  'tickets.statuses.none': 'Geen statusse in hierdie stadium nie.',
  'tickets.statuses.category': 'Stadium',
  'tickets.statuses.clientLabel': 'Kliëntbewoording',
  'tickets.statuses.clientLabelHint': 'Wat die kliënt vertel word, soos "Ons werk daaraan".',
  'tickets.statuses.forType': 'Vir',
  'tickets.statuses.everyType': 'Elke soort',
  'tickets.statuses.isDefault': 'Verstek vir nuwe kaartjies',
  'tickets.statuses.isDefaultHint': 'Nuwe kaartjies begin met hierdie status.',
  'tickets.statuses.orderHint': 'Die eerste status van \'n stadium word gebruik wanneer \'n kaartjie daarheen skuif.',
  'tickets.statuses.categoryHint.new': 'Pas oopgemaak. Niemand het nog gekyk nie.',
  'tickets.statuses.categoryHint.open': 'Jou span werk daaraan.',
  'tickets.statuses.categoryHint.pending': 'Wag vir die kliënt. Die reaksieklok staan stil.',
  'tickets.statuses.categoryHint.on_hold': 'Wag op iets anders. Die reaksieklok staan stil.',
  'tickets.statuses.categoryHint.solved': 'Klaar. Die kliënt kan steeds antwoord.',
  'tickets.statuses.categoryHint.closed': 'Heeltemal afgehandel.',
  'tickets.priorities.title': 'Prioriteite',
  'tickets.priorities.intro': 'Hoe dringend \'n kaartjie is. Reaksieteikens kan per prioriteit verskil.',
  'tickets.priorities.add': 'Voeg prioriteit by',
  'tickets.priorities.empty': 'Nog geen prioriteite nie',
  'tickets.priorities.emptyHint': 'Laag, Normaal, Hoog en Dringend word met die eerste gebruik vir jou gemaak.',
  'tickets.priorities.rank': 'Rang',
  'tickets.priorities.rankHint': 'Hoër is meer dringend.',
  'tickets.priorities.colour': 'Kleur',
  'tickets.priorities.isDefault': 'Verstek',
  'tickets.priorities.isDefaultHint': 'Nuwe kaartjies kry hierdie prioriteit tensy hul soort anders sê.',
  'tickets.rules.title': 'Outomatiserings',
  'tickets.rules.intro': 'Reëls wat kaartjies vanself oopmaak, sorteer en opvolg. Hulle word van bo na onder nagegaan.',
  'tickets.rules.limit': 'Aktief: {{active}} van {{limit}}',
  'tickets.rules.noLimit': 'Aktief: {{active}}',
  'tickets.rules.add': 'Voeg outomatisering by',
  'tickets.rules.new': 'Nuwe outomatisering',
  'tickets.rules.create': 'Skep outomatisering',
  'tickets.rules.empty': 'Nog geen outomatiserings nie',
  'tickets.rules.emptyHint': 'Byvoorbeeld: maak \'n kaartjie oop wanneer \'n kliënt "terugbetaling" skryf, of herinner die span wanneer \'n kaartjie te lank wag.',
  'tickets.rules.orderHint': 'Sleep die reëls, of gebruik die pyltjies, om die volgorde te verander.',
  'tickets.rules.stops': 'Stop hier',
  'tickets.rules.switchFor': 'Skakel {{name}} aan of af',
  'tickets.rules.runs': 'Onlangse lopies',
  'tickets.rules.recentRuns': 'Onlangse lopies',
  'tickets.rules.noRuns': 'Hierdie outomatisering het nog nie geloop nie.',
  'tickets.rules.outcome.done': 'Gedoen',
  'tickets.rules.outcome.skipped': 'Oorgeslaan',
  'tickets.rules.outcome.failed': 'Misluk',
  'tickets.rules.conditionsCount': 'Voorwaardes: {{count}}',
  'tickets.rules.always': 'Altyd',
  'tickets.rules.then': 'Dan: {{actions}}',
  'tickets.rules.deleteBody': '{{name}} sal ophou loop en geskrap word. Dit kan nie ongedaan gemaak word nie.',
  'tickets.rules.toast.on': '{{name}} is aan.',
  'tickets.rules.toast.off': '{{name}} is af.',
  'tickets.rules.toast.saved': '{{name}} gestoor.',
  'tickets.rules.toast.created': '{{name}} geskep.',
  'tickets.rules.when': 'Wanneer',
  'tickets.rules.if': 'As',
  'tickets.rules.thenDo': 'Dan',
  'tickets.rules.allMustMatch': 'Elke voorwaarde moet pas.',
  'tickets.rules.noConditions': 'Geen voorwaardes nie: dit loop elke keer.',
  'tickets.rules.addCondition': 'Voeg \'n voorwaarde by',
  'tickets.rules.hiddenConditions': 'Voorwaardes wat nie op hierdie sneller van toepassing is nie, word weggelaat: {{count}}',
  'tickets.rules.field': 'Veld',
  'tickets.rules.op': 'Vergelyking',
  'tickets.rules.value': 'Waarde',
  'tickets.rules.removeCondition': 'Verwyder hierdie voorwaarde',
  'tickets.rules.removeAction': 'Verwyder hierdie aksie',
  'tickets.rules.noActions': 'Nog geen aksies nie.',
  'tickets.rules.addAction': 'Voeg \'n aksie by…',
  'tickets.rules.minutes': 'Wag al (minute)',
  'tickets.rules.minutesHint': 'Hoe lank die kliënt al wag sonder \'n antwoord van \'n persoon of die kletsbot.',
  'tickets.rules.minutesUnit': 'In minute.',
  'tickets.rules.keywordsPlaceholder': 'terugbetaling, stukkend, kanselleer my bestelling',
  'tickets.rules.keywordsHint': 'Skei met kommas. Hele woorde of frases; hoofletters maak nie saak nie.',
  'tickets.rules.listPlaceholder': 'Eerste, tweede',
  'tickets.rules.listHint': 'Skei met kommas.',
  'tickets.rules.nothingToPick': 'Nog niks om uit te kies nie.',
  'tickets.rules.firstType': 'Die eerste soort',
  'tickets.rules.typeDefault': 'Die soort se verstek',
  'tickets.rules.subjectHint': 'Jy kan {{text}}, {{contact.name}} en {{channel}} gebruik.',
  'tickets.rules.keepTeam': 'Hou die span',
  'tickets.rules.person': 'Persoon',
  'tickets.rules.nobodyInParticular': 'Niemand in die besonder nie',
  'tickets.rules.roundRobin': 'Om die beurt binne die span',
  'tickets.rules.roundRobinHint': 'Wys die spanlid met die minste oop kaartjies toe.',
  'tickets.rules.whichField': 'Veld',
  'tickets.rules.notifyWho': 'Wie om te laat weet',
  'tickets.rules.notify.assignee': 'Wie ook al dit toegewys is',
  'tickets.rules.notify.team': 'Die kaartjie se span',
  'tickets.rules.notify.user': '\'n Bepaalde persoon',
  'tickets.rules.replyText': 'Boodskap aan die kliënt',
  'tickets.rules.replyHint': 'Word op die kaartjie se gesprek gestuur.',
  'tickets.rules.dueIn': 'Sperdatum na',
  'tickets.rules.closeHint': 'Skuif die kaartjie na sy eerste gesluite status.',
  'tickets.rules.stopAfter': 'Stop na hierdie outomatisering',
  'tickets.rules.stopAfterHint': 'Wanneer hierdie een loop, word die outomatiserings daaronder nie nagegaan nie.',
  'tickets.rules.active': 'Aan',
  'tickets.rules.activeHint': 'Net outomatiserings wat aan is, loop. Jou plan beperk hoeveel aan kan wees.',
  'tickets.rules.error.name': 'Gee die outomatisering \'n naam.',
  'tickets.rules.error.minutes': 'Stel hoeveel minute om te wag.',
  'tickets.rules.error.condition': 'Voltooi of verwyder die gemerkte voorwaardes.',
  'tickets.rules.error.noActions': 'Voeg minstens een aksie by.',
  'tickets.rules.error.createTicketTrigger': 'Net \'n outomatisering wat by \'n boodskap begin, kan \'n kaartjie oopmaak.',
  'tickets.rules.error.assign': 'Kies \'n span of \'n persoon.',
  'tickets.rules.error.status': 'Kies \'n status.',
  'tickets.rules.error.priority': 'Kies \'n prioriteit.',
  'tickets.rules.error.tag': 'Tik die etiket.',
  'tickets.rules.error.field': 'Kies \'n veld.',
  'tickets.rules.error.notifyUser': 'Kies wie om te laat weet.',
  'tickets.rules.error.text': 'Skryf die boodskap.',
  'tickets.rules.error.taskTitle': 'Gee die taak \'n naam.',
  'tickets.trigger.message_received': '\'n Kliënt stuur \'n boodskap',
  'tickets.trigger.unanswered': '\'n Kliënt wag sonder \'n antwoord',
  'tickets.trigger.ticket_created': '\'n Kaartjie word oopgemaak',
  'tickets.trigger.ticket_updated': '\'n Kaartjie verander',
  'tickets.trigger.time_passed': 'Tyd verstryk op \'n kaartjie',
  'tickets.trigger.hint.message_received': 'Elke boodskap van \'n kliënt, op elke kanaal.',
  'tickets.trigger.hint.unanswered': '\'n Gesprek waar die kliënt se laaste boodskap vir die minute hieronder geen antwoord gekry het nie.',
  'tickets.trigger.hint.ticket_created': 'Net nadat \'n kaartjie oopgemaak is, deur enigiemand of enigiets.',
  'tickets.trigger.hint.ticket_updated': 'Wanneer \'n kaartjie se status, prioriteit, span, toegewysde, velde of etikette verander.',
  'tickets.trigger.hint.time_passed': 'Word elke vyf minute nagegaan. Gebruik "Minute in hierdie status" om te sê hoe lank.',
  'tickets.op.eq': 'is',
  'tickets.op.neq': 'is nie',
  'tickets.op.in': 'is enige van',
  'tickets.op.contains_any': 'bevat enige van',
  'tickets.op.not_contains_any': 'bevat geeneen van',
  'tickets.op.is_set': 'is ingevul',
  'tickets.op.not_set': 'is nie ingevul nie',
  'tickets.op.gte': 'is minstens',
  'tickets.op.lte': 'is hoogstens',
  'tickets.cond.channel': 'Kanaal',
  'tickets.cond.text': 'Boodskapteks',
  'tickets.cond.contact.tag': 'Kontaketiket',
  'tickets.cond.business_hours': 'Besigheidsure',
  'tickets.cond.ticket.type_id': 'Kaartjiesoort',
  'tickets.cond.ticket.priority_id': 'Kaartjieprioriteit',
  'tickets.cond.ticket.status_category': 'Kaartjiestadium',
  'tickets.cond.ticket.team_id': 'Kaartjiespan',
  'tickets.cond.ticket.tag': 'Kaartjie-etiket',
  'tickets.cond.minutes_in_status': 'Minute in hierdie status',
  'tickets.cond.customField': 'Veld: {{label}}',
  'tickets.cond.hoursOpen': 'Oop',
  'tickets.cond.hoursClosed': 'Gesluit',
  'tickets.action.create_ticket': 'Maak \'n kaartjie oop',
  'tickets.action.assign': 'Wys toe',
  'tickets.action.set_status': 'Stel die status',
  'tickets.action.set_priority': 'Stel die prioriteit',
  'tickets.action.add_tag': 'Voeg \'n etiket by',
  'tickets.action.set_field': 'Vul \'n veld in',
  'tickets.action.notify': 'Laat iemand weet',
  'tickets.action.reply_client': 'Antwoord die kliënt',
  'tickets.action.add_task': 'Voeg \'n taak by',
  'tickets.action.close': 'Sluit die kaartjie'
};

export default tickets;
